<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import ProjectService from "../../services/projectService.js";
import { useDocumentDetails } from "../../composables/useDocumentDetails.js";
import { getFileTypeLabel, getFileTypeBadgeClass } from "../../composables/useFileType.js";

const route = useRoute();
const { visible, documentId, projectId, seedDocument, seedProject, close } = useDocumentDetails();
const projectService = new ProjectService();

const doc = ref(null);
const project = ref(null);
const isLoading = ref(false);

function onEsc(e) { if (e.key === "Escape") close(); }
onMounted(() => document.addEventListener("keydown", onEsc));
onBeforeUnmount(() => document.removeEventListener("keydown", onEsc));

// This panel is mounted once, globally, for the app's lifetime — it isn't
// torn down by navigation on its own, so it was staying open (showing a now
// out-of-context document) across a route change instead of closing, e.g.
// clicking a doc to open its annotation view, or navigating to Projects/Docs/Home.
watch(() => route.fullPath, () => close());

async function load() {
  // Paint instantly from whatever the caller already had in memory. The
  // project/singleton doc list queries now include source/webViewLink too,
  // so a fully-seeded doc (opened from a doc card/menu) never needs a
  // refetch — only a partially-seeded one (e.g. Home's Recent/Starred tabs,
  // whose list query is missing those fields) falls back to getDocument.
  doc.value = seedDocument.value;
  project.value = seedProject.value;
  isLoading.value = !doc.value;
  const isSeedComplete = doc.value && "source" in doc.value && "webViewLink" in doc.value;
  try {
    if (!isSeedComplete) {
      const existingPreview = doc.value?.preview;
      const fresh = await projectService.getDocument({ documentId: documentId.value });
      if (fresh?.id) {
        // The card already has the preview loaded — never make the panel wait
        // on (or swap in a redundant re-fetch of) an image we already have.
        doc.value = { ...doc.value, ...fresh, preview: existingPreview ?? fresh.preview };
      }
    }
    if (!project.value && projectId.value) {
      project.value = await projectService.getProject({ projectId: projectId.value });
    }
  } finally {
    isLoading.value = false;
  }
}

// Watching `visible` alone misses switching documents while already open —
// clicking "View Details" on another file updates documentId/seedDocument
// but doesn't re-trigger a false->true transition on visible, so the panel
// kept showing the previous document. documentId must be a watch source too.
watch([visible, documentId], ([v]) => { if (v) load(); });

const fileTypeLabel = computed(() => getFileTypeLabel(doc.value?.origin?.mimeType ?? doc.value?.originMimeType, doc.value?.name));
const fileTypeBadgeClass = computed(() => getFileTypeBadgeClass(doc.value?.origin?.mimeType ?? doc.value?.originMimeType));

const SOURCE_LABELS = {
  ONE_DRIVE: "OneDrive",
  GOOGLE_DRIVE: "Google Drive",
  BOX: "Box",
  DROPBOX: "Dropbox",
  LOCAL: "Local file",
};
const canViewSource = computed(() => project.value?.userRole !== "VIEWER");
const sourceLabel = computed(() => SOURCE_LABELS[doc.value?.source] ?? doc.value?.source);
const showSourceLink = computed(() => canViewSource.value && doc.value?.source && doc.value.source !== "LOCAL" && doc.value?.webViewLink);

function formatDetailDate(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  const datePart = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const timePart = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${datePart}, at ${timePart}`;
}

function formatFileSize(bytes) {
  if (!bytes) return "—";
  if (bytes <= 1_000_000) return `${(bytes / 1000).toFixed(2)} KB`;
  return `${(bytes / 1_000_000).toFixed(2)} MB`;
}
</script>

<template>
  <div v-if="visible" data-testid="document-details-panel" class="fixed inset-y-0 right-0 w-[340px] bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 shadow-2xl z-[350] flex flex-col">

    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
      <h2 class="text-lg font-bold text-slate-800 dark:text-slate-100 m-0">Details</h2>
      <button
        class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
        data-testid="document-details-close"
        aria-label="Close details"
        @click="close"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <div v-if="isLoading" data-testid="document-details-loading" class="flex-1 flex justify-center pt-16">
      <div class="w-6 h-6 rounded-full border-2 border-slate-200 dark:border-slate-700 border-t-brand animate-spin" />
    </div>

    <div v-else-if="doc" data-testid="document-details-content" class="flex-1 overflow-y-auto p-6">
      <!-- Preview -->
      <div class="relative h-44 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center overflow-hidden mb-4">
        <img data-testid="document-details-preview" v-if="doc.preview" :src="doc.preview" class="h-32 w-auto max-w-[75%] object-contain shadow" />
        <span data-testid="document-details-badge" class="absolute bottom-0 left-0 px-3 py-1.5 rounded-tr-lg text-xs font-bold text-white uppercase" :class="fileTypeBadgeClass">{{ fileTypeLabel }}</span>
      </div>

      <h3 data-testid="document-details-name" class="text-lg font-bold text-slate-800 dark:text-slate-100 m-0 mb-5 break-words">{{ doc.name ?? "Untitled" }}</h3>

      <div class="flex flex-col gap-4">
        <div v-if="doc.uploaderName">
          <p class="text-[13px] text-slate-400 dark:text-slate-500 m-0 mb-1">Uploaded by :</p>
          <p data-testid="document-details-uploaded-by-value" class="text-sm font-medium text-slate-800 dark:text-slate-100 m-0">{{ doc.uploaderName }}</p>
        </div>

        <div>
          <p class="text-[13px] text-slate-400 dark:text-slate-500 m-0 mb-1">Date uploaded :</p>
          <p data-testid="document-details-date-uploaded-value" class="text-sm font-medium text-slate-800 dark:text-slate-100 m-0">{{ formatDetailDate(doc.createdAt) }}</p>
        </div>

        <div>
          <p class="text-[13px] text-slate-400 dark:text-slate-500 m-0 mb-1">Last modified :</p>
          <p data-testid="document-details-last-modified-value" class="text-sm font-medium text-slate-800 dark:text-slate-100 m-0">{{ formatDetailDate(doc.updatedAt) }}</p>
        </div>

        <div>
          <p class="text-[13px] text-slate-400 dark:text-slate-500 m-0 mb-1">File type :</p>
          <p data-testid="document-details-file-type-value" class="text-sm font-medium text-slate-800 dark:text-slate-100 m-0">{{ fileTypeLabel }}</p>
        </div>

        <div>
          <p class="text-[13px] text-slate-400 dark:text-slate-500 m-0 mb-1">Size :</p>
          <p data-testid="document-details-size-value" class="text-sm font-medium text-slate-800 dark:text-slate-100 m-0">{{ formatFileSize(doc.sourceSize) }}</p>
        </div>

        <div v-if="canViewSource && doc.source">
          <p class="text-[13px] text-slate-400 dark:text-slate-500 m-0 mb-1">Source :</p>
          <a
            data-testid="document-details-source-link"
            v-if="showSourceLink"
            :href="doc.webViewLink"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 text-sm font-medium text-blue-500 dark:text-blue-400 hover:underline"
          >
            {{ sourceLabel }}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path d="M7 17L17 7M7 7h10v10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </a>
          <p v-else data-testid="document-details-source-value" class="text-sm font-medium text-slate-800 dark:text-slate-100 m-0">{{ sourceLabel }}</p>
        </div>
      </div>
    </div>
  </div>
</template>
